import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'lib/I18n';
import classNames from 'classnames';
import DOMPurify from 'dompurify';

import ChallengeHeader from 'components/challenge/ChallengeHeader';
import ChallengeFooter from 'components/challenge/ChallengeFooter';
import OrgPickerContainer from 'components/challenge/OrgPickerContainer';
import ExternalAccountConnector from './ExternalAccountConnector';
import SpecialityOrgManager from '../units/SpecialityOrgManager';
import WebComponent from 'components/WebComponent';

export default class Assessment extends React.Component {
  static propTypes = {
    authenticated: PropTypes.bool.isRequired,
    msaSigned: PropTypes.bool.isRequired,
    points: PropTypes.number.isRequired,
    hideOrgPicker: PropTypes.bool,
    isSubmitting: PropTypes.bool.isRequired,
    isSubmitDisabled: PropTypes.func.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    moduleApiName: PropTypes.string.isRequired,
    moduleAnnotation: PropTypes.string,
    unitType: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    renderHelperText: PropTypes.func.isRequired,
    requirements: PropTypes.array,
    selectedOrg: PropTypes.object,
    tpHelpLink: PropTypes.string.isRequired,
    evaluationContext: PropTypes.object.isRequired,
    externalAccountProps: PropTypes.object,
    customTemplateRequired: PropTypes.bool,
    previewMode: PropTypes.bool,
    unitUid: PropTypes.string,
  };

  get orgPickerProps() {
    const isCustomDeOrg = this.props.evaluationContext.inventory_unlimited_custom_org_template_required;

    return {
      apiName: this.props.apiName,
      moduleApiName: this.props.moduleApiName,
      unitUid: this.props.unitUid,
      isCustomDeOrg: isCustomDeOrg,
      templateUri: isCustomDeOrg ? this.props.evaluationContext.org_template_uri : '',
      orgs: this.props.evaluationContext.authorized_accounts,
    };
  }

  isAuthedWithAccountType(accountType) {
    return (
      this.props.authenticated &&
      this.props.evaluationContext.authorized_account_type === accountType
    );
  }

  renderOrgPicker(cssStyle) {
    return (
      <div className={cssStyle}>
        <OrgPickerContainer
          {...this.orgPickerProps}
        />
      </div>
    )
  }

  renderSpecialityOrgManager(cssStyle) {
    return (
      <>
        <SpecialityOrgManager
          unitUid={this.props.unitUid}
          unitApiName={this.props.apiName}
          moduleApiName={this.props.moduleApiName}
          authenticated={this.props.authenticated}
          authorizedAccount={this.props.evaluationContext.authorized_accounts.find(account => account.template_match)}
          productLabel={this.props.evaluationContext.metadata?.authorization_requirements?.product_label}
          previewMode={this.props.previewMode}
        >
        </SpecialityOrgManager>
        {this.props.previewMode && this.renderOrgPicker(cssStyle)}
      </>
    );
  }

  renderChallengeAccountManager(accountType, unitType) {
    const cssStyle = unitType === 'step' ? 'th-challenge__requirements th-challenge__requirements-footer' : 'th-challenge__requirements-footer';

    // OrgFarm use case
    if (accountType === 'salesforce_org' && this.props.customTemplateRequired && this.props.evaluationContext.inventory_limited_custom_org_template_required) {
      if (unitType === 'unit') {
        return (
          <>
            {this.renderSpecialityOrgManager(cssStyle)}
          </>
        )
      } else {
        return (
          <div className="th-challenge__requirements slds-m-top_large">
            {this.renderSpecialityOrgManager(cssStyle)}
          </div>
        )
      }
    }

    if (!this.isAuthedWithAccountType(accountType)) {
      return "";
    }

    if (accountType === 'salesforce_org') {
      return this.renderOrgPicker(cssStyle);
    } else if (['mulesoft_anypoint_account', 'slack_developer_account'].includes(accountType)) {
      return (
        <div className={cssStyle}>
          <ExternalAccountConnector
              moduleApiName={this.props.moduleApiName}
              unitApiName={this.props.apiName}
              {...this.props.externalAccountProps}
              {...this.props.evaluationContext}
          />
        </div>
      );
    }
  }

  renderUnitChallenge = () => (
    <div>
      <h3 className="slds-text-title_caps slds-m-bottom_medium slds-m-top_large">
        {t('challenge.assessment.challenge')}
      </h3>

      <div className="th-challenge__requirements slds-m-top_large">
        {(this.props.title || this.props.description) && <div className="th-challenge__requirements-content">
          {this.props.title && <div className="slds-text-heading_small tds-text_bold slds-m-bottom_large">
            {this.props.title}
          </div>}

          {this.props.description && <div
            className="th-challenge__description"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: this.props.description }}
          />}

          {this.renderRequirements()}
        </div>}

        {this.renderChallengeAccountManager(this.props.evaluationContext.authorized_account_type, 'unit')}
      </div>
    </div>
  );

  renderRequirements = () =>
    this.props.requirements.length > 0 && (
      <ul className="slds-m-top_large">
        {this.props.requirements.map((r, idx) => (
          <li
            key={`req_${idx}`}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r, {ADD_ATTR: ['target']}) }}
          />
        ))}
      </ul>
    );

  renderUnitChallengeHeading = () => (
    <h3 className="slds-text-title_caps slds-m-bottom_medium">
      {t('challenge.assessment.get_ready')}
    </h3>
  );

  renderHandsOnChallengeHelpText = (challengeType, evaluationContext) => {
    if (['mulesoft_anypoint_account', 'slack_developer_account'].includes(evaluationContext)) {
      const serviceName = evaluationContext === 'slack_developer_account' ? 'slack' : 'mulesoft';
      return (
        <p className="slds-m-bottom--small">
          {t(`challenge.assessment.${serviceName}.explanation`, {
            type: t(`challenge.assessment.unitType.${challengeType}`),
          })}
        </p>
      );
    }
    if (evaluationContext === 'salesforce_org' && this.props.customTemplateRequired) {
      return (<p className="slds-m-bottom--small">
        {t('challenge.assessment.explanation_generic', {
          productLabel: this.props.evaluationContext.metadata?.authorization_requirements?.product_label,
        })}
      </p>
      );
    }
    return (
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: t('challenge.assessment.explanation', {
            type: t(`challenge.assessment.unitType.${challengeType}`),
          }),
        }}
      />
    );
  };

  render() {
    const challengeTypeText = (unitType) => (
      unitType === 'unit'
        ? 'unit'
        : 'project'
    );
    const shouldRenderUnitChallenge =
      this.props.unitType === "unit" &&
      (this.props.evaluationContext.authorized_account_type === "salesforce_org" || this.props.authenticated);

    return (
      <section className="th-challenge slds-m-top--xx-large">
        <form onSubmit={this.props.onSubmit}>
          <ChallengeHeader
            points={this.props.points}
            heading={t(`challenge.assessment.${this.props.unitType}.title`)}
          />

          <div
            className={classNames('slds-m-around_large', {
              'slds-m-around_none': this.props.hideOrgPicker,
            })}
          >
            {this.props.moduleAnnotation === 'cannot_complete' && (
              <div className="slds-m-vertical_large">
                <WebComponent
                  tagName="th-inline-toast-message"
                  attrs={{
                    templateName: 'badgeCompletionServiceDisruption',
                  }}
                />
              </div>
            )}

            {this.props.unitType === 'unit' &&
              this.renderUnitChallengeHeading()}

            {
              !this.props.hideOrgPicker &&
              this.renderHandsOnChallengeHelpText(
                challengeTypeText(this.props.unitType),
                this.props.evaluationContext.authorized_account_type
              )
            }

            {shouldRenderUnitChallenge && this.renderUnitChallenge()}

            {this.props.unitType === "step" && this.renderChallengeAccountManager(this.props.evaluationContext.authorized_account_type, 'step')}
          </div>

          <ChallengeFooter
            {...this.props}
            selectedOrg={this.props.selectedOrg}
            hideOrgPicker={this.props.hideOrgPicker}
            type="assessment"
            unitType={this.props.unitType}
          />
        </form>
      </section>
    );
  }
}
